import { useState, useMemo, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator, Check, Plus, Minus, MapPin, Search, Users, Send, MessageCircle, ChevronDown, Clock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { getWhatsAppUrl } from "@/lib/whatsapp";
import { useEnquiryPopup } from "@/contexts/EnquiryPopupContext";

const DURATION_OPTIONS = [
  { value: "3", label: "3 Days / 2 Nights", days: 3 },
  { value: "4", label: "4 Days / 3 Nights", days: 4 },
  { value: "5", label: "5 Days / 4 Nights", days: 5 },
  { value: "6", label: "6 Days / 5 Nights", days: 6 },
  { value: "7", label: "7 Days / 6 Nights", days: 7 },
  { value: "8", label: "8 Days / 7 Nights", days: 8 },
  { value: "10", label: "10 Days / 9 Nights", days: 10 },
];

export interface Destination {
  name: string;
  basePrice: number;
  category: "International" | "Domestic";
}

// Comprehensive destination data with base starting prices (per person)
const DESTINATIONS: Destination[] = [
  // International
  { name: "Dubai", basePrice: 80000, category: "International" },
  { name: "Maldives", basePrice: 120000, category: "International" },
  { name: "Bali", basePrice: 70000, category: "International" },
  { name: "Thailand", basePrice: 45000, category: "International" },
  { name: "Vietnam", basePrice: 50000, category: "International" },
  { name: "Singapore", basePrice: 85000, category: "International" },
  { name: "Malaysia", basePrice: 55000, category: "International" },
  { name: "Europe", basePrice: 250000, category: "International" },
  { name: "Switzerland", basePrice: 300000, category: "International" },
  { name: "Turkey", basePrice: 150000, category: "International" },
  { name: "Australia", basePrice: 350000, category: "International" },
  { name: "Japan", basePrice: 280000, category: "International" },
  { name: "Sri Lanka", basePrice: 50000, category: "International" },
  { name: "Baku", basePrice: 65000, category: "International" },
  { name: "Almaty", basePrice: 70000, category: "International" },
  { name: "Mauritius", basePrice: 130000, category: "International" },
  // Domestic
  { name: "Kashmir", basePrice: 40000, category: "Domestic" },
  { name: "Kerala", basePrice: 35000, category: "Domestic" },
  { name: "Goa", basePrice: 25000, category: "Domestic" },
  { name: "Himachal", basePrice: 30000, category: "Domestic" },
  { name: "Andaman", basePrice: 55000, category: "Domestic" },
  { name: "Rajasthan", basePrice: 35000, category: "Domestic" },
  { name: "North East", basePrice: 45000, category: "Domestic" },
  { name: "Ladakh", basePrice: 50000, category: "Domestic" },
];

const TRAVEL_STYLES = [
  { id: "budget", label: "Budget", multiplier: 0.7, description: "Value stays & transport" },
  { id: "standard", label: "Standard", multiplier: 1.0, description: "Comfortable & balanced" },
  { id: "luxury", label: "Luxury", multiplier: 1.8, description: "Premium experience" },
];

interface GuestBreakdown {
  adults: number;
  children: number;
  infants: number;
}

interface BudgetCalculatorProps {
  onRequestItinerary?: (destination: string, guestDetails?: string) => void;
}

export const BudgetCalculator = ({ onRequestItinerary }: BudgetCalculatorProps) => {
  const { openWithDestination } = useEnquiryPopup();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDestination, setSelectedDestination] = useState<Destination | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [guests, setGuests] = useState<GuestBreakdown>({ adults: 2, children: 0, infants: 0 });
  const [isGuestPopoverOpen, setIsGuestPopoverOpen] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState("standard");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [selectedDuration, setSelectedDuration] = useState("5");
  const totalTravelers = guests.adults + guests.children + guests.infants;
  const durationDays = DURATION_OPTIONS.find(d => d.value === selectedDuration)?.days || 5;
  const durationLabel = DURATION_OPTIONS.find(d => d.value === selectedDuration)?.label || "5 Days / 4 Nights";

  // Filter destinations based on search - group by category
  const filteredDestinations = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return DESTINATIONS;
    return DESTINATIONS.filter((dest) =>
      dest.name.toLowerCase().includes(query) ||
      dest.category.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Group destinations by category for display
  const groupedDestinations = useMemo(() => {
    const international = filteredDestinations.filter(d => d.category === "International");
    const domestic = filteredDestinations.filter(d => d.category === "Domestic");
    return { international, domestic };
  }, [filteredDestinations]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectDestination = (dest: Destination) => {
    setSelectedDestination(dest);
    setSearchQuery(dest.name);
    setIsDropdownOpen(false);
  };

  const updateGuests = (type: keyof GuestBreakdown, delta: number) => {
    setGuests(prev => {
      const newValue = prev[type] + delta;
      if (type === "adults" && newValue < 1) return prev;
      if (newValue < 0) return prev;
      if (newValue > 10) return prev;
      return { ...prev, [type]: newValue };
    });
  };

  // Calculate cost: perDayRate × days × effective travelers
  const calculateCost = (styleMultiplier: number) => {
    if (!selectedDestination) return 0;
    const perDayRate = selectedDestination.basePrice / 5 * styleMultiplier; // base price is for ~5 days
    const adultsCost = guests.adults * perDayRate * durationDays;
    const childrenCost = guests.children * perDayRate * durationDays * 0.75;
    const infantsCost = guests.infants * perDayRate * durationDays * 0.10;
    return Math.round(adultsCost + childrenCost + infantsCost);
  };

  // Per-person-per-day rate for display
  const getPerPersonPerDay = (styleMultiplier: number) => {
    if (!selectedDestination) return 0;
    return Math.round(selectedDestination.basePrice / 5 * styleMultiplier);
  };

  // All three pricing tiers
  const pricingTiers = useMemo(() => {
    return TRAVEL_STYLES.map(style => ({
      ...style,
      cost: calculateCost(style.multiplier),
      perPersonPerDay: getPerPersonPerDay(style.multiplier),
    }));
  }, [selectedDestination, guests, durationDays]);

  const selectedTierCost = pricingTiers.find(t => t.id === selectedStyle)?.cost || 0;

  const formatCurrency = (amount: number) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    }
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatFullCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getGuestSummary = () => {
    const parts: string[] = [];
    if (guests.adults > 0) parts.push(`${guests.adults} Adult${guests.adults > 1 ? 's' : ''}`);
    if (guests.children > 0) parts.push(`${guests.children} Child${guests.children > 1 ? 'ren' : ''}`);
    if (guests.infants > 0) parts.push(`${guests.infants} Infant${guests.infants > 1 ? 's' : ''}`);
    return parts.join(', ') || '2 Adults';
  };

  const getGuestDetailsForItinerary = () => {
    return `${guests.adults} Adults, ${guests.children} Children (2-12 yrs), ${guests.infants} Infants (0-2 yrs)`;
  };

  const handleRequestItinerary = () => {
    if (selectedDestination) {
      onRequestItinerary?.(selectedDestination.name, getGuestDetailsForItinerary());
    }
  };

  // Always route to the Universal Enquiry Form (no booking/pricing flow).
  const handleOpenEnquiry = () => {
    const destinationLabel = selectedDestination
      ? `${selectedDestination.name} — ${durationDays} Days, ${getGuestSummary()}`
      : "Custom Trip Enquiry";
    if (onRequestItinerary) {
      onRequestItinerary(destinationLabel, getGuestDetailsForItinerary());
    } else {
      openWithDestination(destinationLabel);
    }
  };

  return (
    <section className="section-padding bg-background" aria-labelledby="budget-heading">
      <div className="container-luxury">
        <div className="text-center space-y-4 mb-14">
          <span className="section-badge">
            <Calculator className="inline-block w-4 h-4 mr-1 -mt-0.5" />
            Trip Planner
          </span>
          <h2
            id="budget-heading"
            className="section-title"
          >
            Plan Your Budget
          </h2>
          <p className="section-subtitle">
            Get an instant estimate for your dream vacation. Search destinations and adjust options for real-time pricing.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="luxury-card p-6 md:p-8 space-y-6">
            {/* Inputs: Destination + Duration + Travelers */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {/* Searchable Destination Combobox */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  Search Destination
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    ref={inputRef}
                    type="text"
                    placeholder="e.g., Dubai, Bali, Kashmir"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setIsDropdownOpen(true);
                      if (!e.target.value) setSelectedDestination(null);
                    }}
                    onFocus={() => setIsDropdownOpen(true)}
                    className="pl-10 h-12 text-base"
                  />
                  <AnimatePresence>
                    {isDropdownOpen && filteredDestinations.length > 0 && (
                      <motion.div
                        ref={dropdownRef}
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-lg shadow-xl z-50 max-h-72 overflow-y-auto"
                      >
                        {groupedDestinations.international.length > 0 && (
                          <div>
                            <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-muted/50 sticky top-0">
                              International
                            </div>
                            {groupedDestinations.international.map((dest) => (
                              <button
                                key={dest.name}
                                type="button"
                                onClick={() => handleSelectDestination(dest)}
                                className={cn(
                                  "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors cursor-pointer hover:bg-muted",
                                  selectedDestination?.name === dest.name && "bg-primary/10"
                                )}
                              >
                                <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
                                <div className="flex-1">
                                  <div className="font-medium">{dest.name}</div>
                                  <div className="text-xs text-muted-foreground">
                                    From {formatCurrency(dest.basePrice)}/person
                                  </div>
                                </div>
                                {selectedDestination?.name === dest.name && (
                                  <Check className="h-4 w-4 text-primary" />
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {groupedDestinations.domestic.length > 0 && (
                          <div>
                            <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-muted/50 sticky top-0">
                              India (Domestic)
                            </div>
                            {groupedDestinations.domestic.map((dest) => (
                              <button
                                key={dest.name}
                                type="button"
                                onClick={() => handleSelectDestination(dest)}
                                className={cn(
                                  "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors cursor-pointer hover:bg-muted",
                                  selectedDestination?.name === dest.name && "bg-primary/10"
                                )}
                              >
                                <MapPin className="h-4 w-4 text-amber-600 flex-shrink-0" />
                                <div className="flex-1">
                                  <div className="font-medium">{dest.name}</div>
                                  <div className="text-xs text-muted-foreground">
                                    From {formatCurrency(dest.basePrice)}/person
                                  </div>
                                </div>
                                {selectedDestination?.name === dest.name && (
                                  <Check className="h-4 w-4 text-primary" />
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Trip Duration Selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  Trip Duration
                </label>
                <Select value={selectedDuration} onValueChange={setSelectedDuration}>
                  <SelectTrigger className="h-12 text-base">
                    <SelectValue placeholder="Select trip duration" />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATION_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* MMT-Style Guest Selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  Travelers
                </label>
                <Popover open={isGuestPopoverOpen} onOpenChange={setIsGuestPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-12 justify-between text-base font-normal"
                    >
                      <span className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        {getGuestSummary()}
                      </span>
                      <ChevronDown className={cn(
                        "h-4 w-4 text-muted-foreground transition-transform",
                        isGuestPopoverOpen && "rotate-180"
                      )} />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-4" align="start">
                    <div className="space-y-4">
                      <h4 className="font-medium text-sm text-foreground">Select Travelers</h4>
                      
                      {/* Adults */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">Adults</p>
                          <p className="text-xs text-muted-foreground">12+ years</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 rounded-full"
                            onClick={() => updateGuests("adults", -1)}
                            disabled={guests.adults <= 1}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-6 text-center font-semibold">{guests.adults}</span>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 rounded-full"
                            onClick={() => updateGuests("adults", 1)}
                            disabled={guests.adults >= 10}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>

                      {/* Children */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">Children</p>
                          <p className="text-xs text-muted-foreground">2-12 years (75% price)</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 rounded-full"
                            onClick={() => updateGuests("children", -1)}
                            disabled={guests.children <= 0}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-6 text-center font-semibold">{guests.children}</span>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 rounded-full"
                            onClick={() => updateGuests("children", 1)}
                            disabled={guests.children >= 10}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>

                      {/* Infants */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">Infants</p>
                          <p className="text-xs text-muted-foreground">0-2 years (10% price)</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 rounded-full"
                            onClick={() => updateGuests("infants", -1)}
                            disabled={guests.infants <= 0}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-6 text-center font-semibold">{guests.infants}</span>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 rounded-full"
                            onClick={() => updateGuests("infants", 1)}
                            disabled={guests.infants >= 10}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>

                      <div className="pt-2 border-t border-border">
                        <Button 
                          className="w-full" 
                          size="sm"
                          onClick={() => setIsGuestPopoverOpen(false)}
                        >
                          Done
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* 3-Column Pricing Comparison Table */}
            <AnimatePresence mode="wait">
              {selectedDestination ? (
                <motion.div
                  key="pricing-table"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-4"
                >
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                      Estimated cost for <span className="font-semibold text-foreground">{getGuestSummary()}</span> for <span className="font-semibold text-foreground">{durationDays} Days</span> in <span className="font-semibold text-primary">{selectedDestination.name}</span>
                    </p>
                    {(guests.children > 0 || guests.infants > 0) && (
                      <p className="text-xs text-muted-foreground mt-1">
                        (Children 75% • Infants 10% of adult fare)
                      </p>
                    )}
                  </div>

                  {/* Pricing Cards Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                    {pricingTiers.map((tier) => (
                      <motion.button
                        key={tier.id}
                        type="button"
                        onClick={() => setSelectedStyle(tier.id)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={cn(
                          "relative p-4 md:p-5 rounded-xl border-2 text-center transition-all cursor-pointer",
                          selectedStyle === tier.id
                            ? "border-primary bg-primary/5 shadow-lg ring-2 ring-primary/20"
                            : "border-border hover:border-primary/40 bg-card"
                        )}
                      >
                        {selectedStyle === tier.id && (
                          <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                            Selected
                          </div>
                        )}
                        <div className="space-y-2 pt-1">
                          <h4 className={cn(
                            "font-semibold text-base",
                            selectedStyle === tier.id ? "text-primary" : "text-foreground"
                          )}>
                            {tier.label}
                          </h4>
                          <p className="text-xs text-muted-foreground">{tier.description}</p>
                          <p className={cn(
                            "text-2xl md:text-3xl font-bold",
                            selectedStyle === tier.id ? "text-primary" : "text-foreground"
                          )}>
                            {formatCurrency(tier.cost)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Total Package
                          </p>
                          <p className="text-[11px] text-muted-foreground/70 mt-1">
                            {formatCurrency(tier.perPersonPerDay)}/person/day
                          </p>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="placeholder"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-muted/30 rounded-xl p-8 text-center border border-dashed border-border"
                >
                  <MapPin className="h-10 w-10 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    Search and select a destination above to see pricing options
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Action Card - Next Steps */}
            <AnimatePresence>
              {selectedDestination && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  className="bg-card border-2 border-gold/30 rounded-xl p-5 md:p-6 text-center space-y-4"
                >
                  <div className="space-y-2">
                    <p className="text-lg font-semibold text-foreground">
                      This budget fits your style! ✨
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Want a detailed day-wise plan for your {durationDays}-day {pricingTiers.find(t => t.id === selectedStyle)?.label} {selectedDestination.name} trip?
                    </p>
                  </div>
                  
                  <div className="flex flex-row gap-3 justify-center flex-wrap">
                    <Button
                      onClick={handleOpenEnquiry}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium px-6"
                    >
                      <Send className="mr-2 h-4 w-4" />
                      Send Enquiry
                    </Button>
                    <Button
                      variant="outline"
                      asChild
                      className="border-gold text-gold hover:bg-gold/10"
                    >
                      <a
                        href={getWhatsAppUrl(`Hello Feel Free Holidays! I used your Budget Calculator and I'd like to enquire about a ${durationDays}-day ${pricingTiers.find(t => t.id === selectedStyle)?.label} trip to ${selectedDestination.name} for ${getGuestSummary()}. Please share more details.`)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <MessageCircle className="mr-2 h-4 w-4" />
                        Enquire on WhatsApp
                      </a>
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <p className="text-xs text-center text-muted-foreground">
              * Estimates only — share your details via the enquiry form and our team will craft a custom quote and itinerary for you.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
